"use client"

interface CategoryFilterPillProps {
  /** The visible label of the pill (e.g. "Energy & Cost") */
  label: string
  /** A count shown in a small chip inside the pill */
  count: number
  /** Whether the pill is the currently selected filter */
  active?: boolean
  /** Handler fired when the pill is clicked */
  onClick?: () => void
  /** Additional classes applied to the outer button */
  className?: string
}

/**
 * CategoryFilterPill — rounded button with an inline count badge, used by
 * category/filter navigation bars (e.g. the sticky filter strip on `/tools`).
 *
 * Variants:
 * - Inactive: white bg, gray border, gray count chip; hover goes orange.
 * - Active: fridge-orange bg, near-black text (FRIDGE-142), translucent white
 *   count chip in the same near-black, orange glow shadow.
 *
 * This is an *interactive* pill — do not confuse it with `<FilterPill>`,
 * which is a display-only chip with an optional remove button used to show
 * already-applied filters.
 */
export function CategoryFilterPill({
  label,
  count,
  active = false,
  onClick,
  className = "",
}: CategoryFilterPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 sm:gap-2 rounded-full px-3 sm:px-4 py-1 sm:py-2 text-[12px] sm:text-[13px] font-semibold tracking-[0.01em] transition-all duration-150",
        active
          ? "bg-[#ff9951] text-[var(--color-brand-action-text)] border border-[#ff9951] shadow-[0_6px_14px_-6px_rgba(255,153,81,0.45)]"
          : "bg-white text-gray-900 border border-gray-200 shadow-sm hover:border-[#ff9951] hover:text-[#ff9951]",
        className,
      ].join(" ")}
    >
      <span>{label}</span>
      <span
        className={[
          "rounded-full px-2 py-[1px] text-[11px] font-bold font-mono",
          active ? "bg-white/25 text-[var(--color-brand-action-text)]" : "bg-gray-100 text-[#697080]",
        ].join(" ")}
      >
        {count}
      </span>
    </button>
  )
}
